// Access / Travel info page — Google Maps embed + transport options + shuttle info for the Feasts.
import { useEffect } from "react";
import { useI18n } from "@/i18n/I18nContext";
import Ornament from "@/components/Ornament";
import SeoHead from "@/components/SeoHead";

const SEO_TITLES = {
  fr: "Accès au Prieuré saint Léonard — Voiture, train, car, avion, vélo, navettes",
  en: "Access to the Priory of Saint Léonard — Car, train, bus, plane, bike, shuttles",
  it: "Come arrivare al Priorato di San Leonardo — Auto, treno, autobus, aereo, bici",
  es: "Cómo llegar al Priorato de San Leonardo — Coche, tren, autobús, avión, bici",
  de: "Anreise zum Priorat Saint Léonard — Auto, Zug, Bus, Flugzeug, Fahrrad",
  pt: "Como chegar ao Priorado de São Leonardo — Carro, comboio, autocarro, avião, bicicleta",
  zh: "如何前往圣莱奥纳尔修道院 — 汽车、火车、巴士、飞机、自行车、班车",
  pl: "Jak dojechać do Klasztoru św. Leonarda — Samochód, pociąg, autobus, samolot, rower",
};
const SEO_DESC = {
  fr: "Toutes les façons de venir au Prieuré saint Léonard à Vains, dans la baie du Mont-Saint-Michel. Carte Google Maps, navettes gratuites pendant les Fêtes 2027.",
  en: "All the ways to come to the Priory of Saint Léonard in Vains, Mont-Saint-Michel Bay. Google Maps, free shuttles during the 2027 Feasts.",
  it: "Tutti i modi per arrivare al Priorato di San Leonardo a Vains, nella baia di Mont-Saint-Michel.",
  es: "Todas las formas de llegar al Priorato de San Leonardo en Vains, bahía del Mont-Saint-Michel.",
  de: "Alle Wege zum Priorat Saint Léonard in Vains, Bucht des Mont-Saint-Michel.",
  pt: "Todas as formas de chegar ao Priorado de São Leonardo em Vains, baía do Monte Saint-Michel.",
  zh: "前往位于圣米歇尔山湾凡的圣莱奥纳尔修道院的所有方式。",
  pl: "Wszystkie sposoby dotarcia do Klasztoru św. Leonarda w Vains, zatoka Mont-Saint-Michel.",
};

// Multilingual copy
const T = {
  fr: {
    kicker: "Accès au Prieuré saint Léonard",
    title: "Venir au Prieuré",
    intro: "Au cœur de la baie du Mont-Saint-Michel, le Prieuré saint Léonard se découvre au bout d'un chemin de campagne, sur un promontoire qui domine la mer. Voici les meilleurs itinéraires pour nous rejoindre.",
    addressTitle: "Adresse & coordonnées GPS",
    address: "Prieuré saint Léonard, 50300 Vains, France",
    gps: "48°37′47″N · 1°22′02″W",
    openInMaps: "Ouvrir dans Google Maps",
    openInWaze: "Ouvrir dans Waze",
    openInApple: "Ouvrir dans Plans (Apple)",
    transportsTitle: "Comment venir",
    carTitle: "En voiture",
    carBody: "Depuis Paris : A13 puis A84 sortie 37 (Avranches), puis D911 direction Saint-Jean-le-Thomas. Comptez 3h30. Depuis Caen : A84 sud, 1h30. Depuis Rennes : A84 nord, 1h.",
    trainTitle: "En train",
    trainBody: "Gare SNCF d'Avranches (ligne Paris–Granville). Liaisons quotidiennes depuis Paris Montparnasse (≈ 3h00). Le Prieuré est à 12 km de la gare (taxi ≈ 25 €).",
    busTitle: "En car",
    busBody: "Réseau Nomad Car (Région Normandie) : lignes 304/305 depuis Avranches/Saint-Hilaire et Granville/Genêts. Arrêt « Vains » à 800 m du Prieuré.",
    planeTitle: "En avion",
    planeBody: "Aéroports les plus proches : Caen-Carpiquet (1h30 en voiture), Rennes-Saint-Jacques (1h15), Dinard (1h45), Paris CDG/Orly (4h en TGV+train).",
    bikeTitle: "À vélo",
    bikeBody: "Itinéraire balisé EV4 Vélomaritime depuis Saint-Malo, Avranches ou le Mont-Saint-Michel. Local vélo sur place pendant les fêtes.",
    shuttleTitle: "Navettes pendant les Fêtes de saint Léonard",
    shuttleBadge: "2 · 3 · 4 juillet 2027",
    shuttleBody: "Pendant les trois jours des Fêtes, des navettes gratuites sont mises en place vers le Prieuré depuis les principaux points d'accès de la baie.",
    shuttleStops: [],
    shuttleNote: "Modalités et horaires précisés ultérieurement aux inscrits.",
    parkingTitle: "Stationnement & accès",
    parkingBody: "Pour les Fêtes, le stationnement se fait sur des parkings-relais d'où partent les navettes, afin de préserver le site.",
    accessibilityTitle: "Accessibilité",
    accessibilityBody: "Le site est en partie accessible aux personnes à mobilité réduite. Pour toute demande spécifique (fauteuil, accompagnement), écrivez-nous : contact@prieure-saint-leonard.fr.",
    questionsTitle: "Une question sur votre venue ?",
    questionsCta: "Nous contacter",
  },
  en: {
    kicker: "Access to Priory of Saint Léonard",
    title: "Getting to the Priory",
    intro: "In the heart of Mont-Saint-Michel Bay, the Priory of Saint Léonard reveals itself at the end of a country lane, on a promontory overlooking the sea. Here are the best itineraries to join us.",
    addressTitle: "Address & GPS coordinates",
    address: "Prieuré saint Léonard, 50300 Vains, France",
    gps: "48°37′47″N · 1°22′02″W",
    openInMaps: "Open in Google Maps",
    openInWaze: "Open in Waze",
    openInApple: "Open in Apple Maps",
    transportsTitle: "How to come",
    carTitle: "By car",
    carBody: "From Paris: A13 then A84 exit 37 (Avranches), then D911 toward Saint-Jean-le-Thomas. About 3h30. From Caen: A84 south, 1h30. From Rennes: A84 north, 1h.",
    trainTitle: "By train",
    trainBody: "SNCF station of Avranches (Paris–Granville line). Daily connections from Paris Montparnasse (≈ 3h00). The Priory is 12 km from the station (taxi ≈ €25).",
    busTitle: "By bus",
    busBody: "Nomad Car network (Normandy Region): lines 304/305 from Avranches/Saint-Hilaire and Granville/Genêts. Stop « Vains », 800 m from the Priory.",
    planeTitle: "By plane",
    planeBody: "Nearest airports: Caen-Carpiquet (1h30 by car), Rennes-Saint-Jacques (1h15), Dinard (1h45), Paris CDG/Orly (4h by high-speed train + train).",
    bikeTitle: "By bike",
    bikeBody: "Signposted EV4 Vélomaritime route from Saint-Malo, Avranches or Mont-Saint-Michel. Secure bike storage during the Feasts.",
    shuttleTitle: "Shuttles during the Feasts of Saint Léonard",
    shuttleBadge: "2 · 3 · 4 July 2027",
    shuttleBody: "Throughout the three days of the Feasts, free shuttles will run to the Priory from the main access points of the bay.",
    shuttleStops: [],
    shuttleNote: "Details and schedule communicated later to registered participants.",
    parkingTitle: "Parking & access",
    parkingBody: "During the Feasts, parking is on park-and-ride lots from which the shuttles depart, to preserve the site.",
    accessibilityTitle: "Accessibility",
    accessibilityBody: "The site is partly accessible to people with reduced mobility. For any specific request (wheelchair, assistance), please write to: contact@prieure-saint-leonard.fr.",
    questionsTitle: "A question about your trip?",
    questionsCta: "Contact us",
  },
  it: {
    kicker: "Accesso al Priorato di San Leonardo",
    title: "Come arrivare al Priorato",
    intro: "Nel cuore della baia di Mont-Saint-Michel, il Priorato di San Leonardo si scopre al termine di una strada di campagna, su un promontorio che domina il mare. Ecco i migliori itinerari per raggiungerci.",
    addressTitle: "Indirizzo e coordinate GPS",
    address: "Prieuré saint Léonard, 50300 Vains, Francia",
    gps: "48°37′47″N · 1°22′02″W",
    openInMaps: "Apri in Google Maps",
    openInWaze: "Apri in Waze",
    openInApple: "Apri in Mappe Apple",
    transportsTitle: "Come venire",
    carTitle: "In auto",
    carBody: "Da Parigi: A13 poi A84 uscita 37 (Avranches), poi D911 in direzione Saint-Jean-le-Thomas. Circa 3h30. Da Caen: A84 sud, 1h30. Da Rennes: A84 nord, 1h.",
    trainTitle: "In treno",
    trainBody: "Stazione SNCF di Avranches (linea Parigi–Granville). Collegamenti quotidiani da Paris Montparnasse (≈ 3h00). Il Priorato dista 12 km dalla stazione (taxi ≈ 25 €).",
    busTitle: "In autobus",
    busBody: "Rete Nomad Car (Regione Normandia): linee 304/305 da Avranches/Saint-Hilaire e Granville/Genêts. Fermata « Vains », a 800 m dal Priorato.",
    planeTitle: "In aereo",
    planeBody: "Aeroporti più vicini: Caen-Carpiquet (1h30 in auto), Rennes-Saint-Jacques (1h15), Dinard (1h45), Parigi CDG/Orly (4h in TGV+treno).",
    bikeTitle: "In bicicletta",
    bikeBody: "Itinerario segnalato EV4 Vélomaritime da Saint-Malo, Avranches o Mont-Saint-Michel. Deposito biciclette sicuro durante le Feste.",
    shuttleTitle: "Navette durante le Feste di San Leonardo",
    shuttleBadge: "2 · 3 · 4 luglio 2027",
    shuttleBody: "Durante i tre giorni delle Feste, navette gratuite collegheranno il Priorato dai principali punti di accesso della baia.",
    shuttleStops: [],
    shuttleNote: "Modalità e orari comunicati in seguito agli iscritti.",
    parkingTitle: "Parcheggio e accesso",
    parkingBody: "Durante le Feste, il parcheggio avviene nei parcheggi-scambiatore da cui partono le navette, per preservare il sito.",
    accessibilityTitle: "Accessibilità",
    accessibilityBody: "Il sito è in parte accessibile alle persone a mobilità ridotta. Per qualsiasi richiesta specifica (sedia a rotelle, accompagnamento), scrivere a: contact@prieure-saint-leonard.fr.",
    questionsTitle: "Una domanda sul vostro arrivo?",
    questionsCta: "Contattateci",
  },
  es: {
    kicker: "Acceso al Priorato de San Leonardo",
    title: "Cómo llegar al Priorato",
    intro: "En el corazón de la bahía del Mont-Saint-Michel, el Priorato de San Leonardo se descubre al final de un camino rural, sobre un promontorio que domina el mar. He aquí los mejores itinerarios para unirse a nosotros.",
    addressTitle: "Dirección y coordenadas GPS",
    address: "Prieuré saint Léonard, 50300 Vains, Francia",
    gps: "48°37′47″N · 1°22′02″W",
    openInMaps: "Abrir en Google Maps",
    openInWaze: "Abrir en Waze",
    openInApple: "Abrir en Mapas (Apple)",
    transportsTitle: "Cómo venir",
    carTitle: "En coche",
    carBody: "Desde París: A13 luego A84 salida 37 (Avranches), después D911 dirección Saint-Jean-le-Thomas. Unas 3h30. Desde Caen: A84 sur, 1h30. Desde Rennes: A84 norte, 1h.",
    trainTitle: "En tren",
    trainBody: "Estación SNCF de Avranches (línea París–Granville). Conexiones diarias desde París Montparnasse (≈ 3h00). El Priorato está a 12 km de la estación (taxi ≈ 25 €).",
    busTitle: "En autobús",
    busBody: "Red Nomad Car (Región de Normandía): líneas 304/305 desde Avranches/Saint-Hilaire y Granville/Genêts. Parada « Vains », a 800 m del Priorato.",
    planeTitle: "En avión",
    planeBody: "Aeropuertos más cercanos: Caen-Carpiquet (1h30 en coche), Rennes-Saint-Jacques (1h15), Dinard (1h45), París CDG/Orly (4h en TGV+tren).",
    bikeTitle: "En bicicleta",
    bikeBody: "Ruta señalizada EV4 Vélomaritime desde Saint-Malo, Avranches o el Mont-Saint-Michel. Guardabicicletas seguro durante las Fiestas.",
    shuttleTitle: "Lanzaderas durante las Fiestas de San Leonardo",
    shuttleBadge: "2 · 3 · 4 de julio de 2027",
    shuttleBody: "Durante los tres días de las Fiestas, lanzaderas gratuitas conectarán el Priorato desde los principales puntos de acceso de la bahía.",
    shuttleStops: [],
    shuttleNote: "Modalidades y horarios comunicados más adelante a los inscritos.",
    parkingTitle: "Aparcamiento y acceso",
    parkingBody: "Durante las Fiestas, el aparcamiento se realiza en aparcamientos disuasorios desde donde salen las lanzaderas, para preservar el sitio.",
    accessibilityTitle: "Accesibilidad",
    accessibilityBody: "El sitio es parcialmente accesible para personas con movilidad reducida. Para cualquier solicitud específica (silla de ruedas, acompañamiento), escríbanos: contact@prieure-saint-leonard.fr.",
    questionsTitle: "¿Una pregunta sobre su llegada?",
    questionsCta: "Contáctenos",
  },
  de: {
    kicker: "Zugang zum Priorat Saint Léonard",
    title: "Anreise zum Priorat",
    intro: "Im Herzen der Bucht des Mont-Saint-Michel offenbart sich das Priorat Saint Léonard am Ende eines Landweges, auf einem Vorsprung, der das Meer überblickt. Hier sind die besten Anfahrtsrouten, um uns zu erreichen.",
    addressTitle: "Anschrift & GPS-Koordinaten",
    address: "Prieuré saint Léonard, 50300 Vains, Frankreich",
    gps: "48°37′47″N · 1°22′02″W",
    openInMaps: "In Google Maps öffnen",
    openInWaze: "In Waze öffnen",
    openInApple: "In Apple Karten öffnen",
    transportsTitle: "Anreise",
    carTitle: "Mit dem Auto",
    carBody: "Von Paris: A13, dann A84, Ausfahrt 37 (Avranches), dann D911 Richtung Saint-Jean-le-Thomas. Etwa 3h30. Von Caen: A84 Süd, 1h30. Von Rennes: A84 Nord, 1h.",
    trainTitle: "Mit dem Zug",
    trainBody: "SNCF-Bahnhof Avranches (Strecke Paris–Granville). Tägliche Verbindungen von Paris Montparnasse (≈ 3h00). Das Priorat ist 12 km vom Bahnhof entfernt (Taxi ≈ 25 €).",
    busTitle: "Mit dem Bus",
    busBody: "Nomad Car-Netz (Region Normandie): Linien 304/305 von Avranches/Saint-Hilaire und Granville/Genêts. Haltestelle « Vains », 800 m vom Priorat.",
    planeTitle: "Mit dem Flugzeug",
    planeBody: "Nächste Flughäfen: Caen-Carpiquet (1h30 mit dem Auto), Rennes-Saint-Jacques (1h15), Dinard (1h45), Paris CDG/Orly (4h mit TGV+Zug).",
    bikeTitle: "Mit dem Fahrrad",
    bikeBody: "Beschilderter Radweg EV4 Vélomaritime von Saint-Malo, Avranches oder Mont-Saint-Michel. Sicherer Fahrradstellplatz während der Feste.",
    shuttleTitle: "Shuttles während der Feste des heiligen Leonhard",
    shuttleBadge: "2.·3.·4. Juli 2027",
    shuttleBody: "Während der drei Tage der Feste verkehren kostenlose Shuttles zum Priorat von den wichtigsten Zugangspunkten der Bucht.",
    shuttleStops: [],
    shuttleNote: "Modalitäten und Fahrpläne werden den Angemeldeten später mitgeteilt.",
    parkingTitle: "Parken & Zugang",
    parkingBody: "Während der Feste erfolgt das Parken auf Park-and-Ride-Plätzen, von wo aus die Shuttles abfahren, um den Ort zu schonen.",
    accessibilityTitle: "Barrierefreiheit",
    accessibilityBody: "Der Ort ist teilweise für Personen mit eingeschränkter Mobilität zugänglich. Für besondere Anfragen (Rollstuhl, Begleitung), schreiben Sie uns: contact@prieure-saint-leonard.fr.",
    questionsTitle: "Eine Frage zu Ihrer Anreise?",
    questionsCta: "Kontaktieren Sie uns",
  },
  pt: {
    kicker: "Acesso ao Priorado de São Leonardo",
    title: "Como chegar ao Priorado",
    intro: "No coração da baía do Monte Saint-Michel, o Priorado de São Leonardo descobre-se ao fim de um caminho rural, num promontório sobre o mar. Eis os melhores itinerários para nos encontrar.",
    addressTitle: "Morada e coordenadas GPS",
    address: "Prieuré saint Léonard, 50300 Vains, França",
    gps: "48°37′47″N · 1°22′02″W",
    openInMaps: "Abrir no Google Maps",
    openInWaze: "Abrir no Waze",
    openInApple: "Abrir no Apple Mapas",
    transportsTitle: "Como vir",
    carTitle: "De carro",
    carBody: "Desde Paris: A13 depois A84 saída 37 (Avranches), depois D911 direção Saint-Jean-le-Thomas. Cerca de 3h30. Desde Caen: A84 sul, 1h30. Desde Rennes: A84 norte, 1h.",
    trainTitle: "De comboio",
    trainBody: "Estação SNCF de Avranches (linha Paris–Granville). Ligações diárias desde Paris Montparnasse (≈ 3h00). O Priorado fica a 12 km da estação (táxi ≈ 25 €).",
    busTitle: "De autocarro",
    busBody: "Rede Nomad Car (Região da Normandia): linhas 304/305 desde Avranches/Saint-Hilaire e Granville/Genêts. Paragem « Vains », a 800 m do Priorado.",
    planeTitle: "De avião",
    planeBody: "Aeroportos mais próximos: Caen-Carpiquet (1h30 de carro), Rennes-Saint-Jacques (1h15), Dinard (1h45), Paris CDG/Orly (4h em TGV+comboio).",
    bikeTitle: "De bicicleta",
    bikeBody: "Itinerário sinalizado EV4 Vélomaritime desde Saint-Malo, Avranches ou Monte Saint-Michel. Guarda-bicicletas seguro durante as Festas.",
    shuttleTitle: "Transferes (navettes) durante as Festas de São Leonardo",
    shuttleBadge: "2 · 3 · 4 de julho de 2027",
    shuttleBody: "Durante os três dias das Festas, transferes gratuitos ligarão o Priorado a partir dos principais pontos de acesso da baía.",
    shuttleStops: [],
    shuttleNote: "Modalidades e horários comunicados posteriormente aos inscritos.",
    parkingTitle: "Estacionamento e acesso",
    parkingBody: "Durante as Festas, o estacionamento é feito em parques dissuasores de onde partem os transferes, para preservar o local.",
    accessibilityTitle: "Acessibilidade",
    accessibilityBody: "O local é parcialmente acessível a pessoas com mobilidade reduzida. Para qualquer pedido específico (cadeira de rodas, acompanhamento), escreva-nos: contact@prieure-saint-leonard.fr.",
    questionsTitle: "Uma questão sobre a sua vinda?",
    questionsCta: "Contacte-nos",
  },
};

// SVG transport icons — line-art, color-inherit, 32px
const I = {
  car:  (p) => (<svg {...p} viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="1.4"><path d="M4 13l2-5a2 2 0 0 1 1.9-1.3h8.2A2 2 0 0 1 18 8l2 5"/><path d="M3 13h18v5H3z"/><circle cx="7" cy="18" r="1.5"/><circle cx="17" cy="18" r="1.5"/></svg>),
  train:(p) => (<svg {...p} viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="1.4"><rect x="5" y="3" width="14" height="14" rx="3"/><path d="M5 11h14M8 21l2-3M16 21l-2-3"/><circle cx="9" cy="14" r=".8" fill="currentColor"/><circle cx="15" cy="14" r=".8" fill="currentColor"/></svg>),
  bus:  (p) => (<svg {...p} viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="1.4"><rect x="4" y="4" width="16" height="14" rx="2"/><path d="M4 11h16M8 22v-2M16 22v-2"/><circle cx="8" cy="15.5" r=".9" fill="currentColor"/><circle cx="16" cy="15.5" r=".9" fill="currentColor"/></svg>),
  plane:(p) => (<svg {...p} viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="1.4"><path d="M2.5 13l9-2 6-7a2 2 0 0 1 2.7 2.7l-7 6-2 9-2-5-6-2z"/></svg>),
  bike: (p) => (<svg {...p} viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="1.4"><circle cx="5.5" cy="17" r="3.5"/><circle cx="18.5" cy="17" r="3.5"/><path d="M5.5 17l4-9h5l3.5 9M9.5 8h-2M13 8l2 4"/></svg>),
  shuttle:(p) => (<svg {...p} viewBox="0 0 24 24" width="36" height="36" fill="none" stroke="currentColor" strokeWidth="1.4"><rect x="3" y="6" width="18" height="11" rx="2"/><path d="M3 12h18M7 21v-2M17 21v-2"/><circle cx="7" cy="14.5" r=".9" fill="currentColor"/><circle cx="17" cy="14.5" r=".9" fill="currentColor"/><path d="M6 6l1-2h10l1 2"/></svg>),
};

// Vains GPS — used for map embed (no API key needed for /maps/embed/v1/place with q=...)
const GPS_LAT = 48.6464;   // approx Vains village
const GPS_LNG = -1.4286;
const ADDRESS_Q = encodeURIComponent("Prieuré Saint Léonard, Vains, 50300, France");
const MAPS_EMBED = `https://www.google.com/maps?q=${ADDRESS_Q}&z=14&output=embed`;
const MAPS_LINK  = `https://www.google.com/maps/dir/?api=1&destination=${ADDRESS_Q}`;
const WAZE_LINK  = `https://waze.com/ul?ll=${GPS_LAT},${GPS_LNG}&navigate=yes`;
const APPLE_LINK = `https://maps.apple.com/?daddr=${ADDRESS_Q}`;

export default function Access() {
  const { locale } = useI18n();
  const tr = T[locale] || T.fr;

  useEffect(() => { window.scrollTo({ top: 0 }); }, []);

  const transports = [
    { Icon: I.car,   title: tr.carTitle,   body: tr.carBody,   testid: "car" },
    { Icon: I.train, title: tr.trainTitle, body: tr.trainBody, testid: "train" },
    { Icon: I.bus,   title: tr.busTitle,   body: tr.busBody,   testid: "bus" },
    { Icon: I.plane, title: tr.planeTitle, body: tr.planeBody, testid: "plane" },
    { Icon: I.bike,  title: tr.bikeTitle,  body: tr.bikeBody,  testid: "bike" },
  ];

  return (
    <div data-testid="access-page">
      <SeoHead titles={SEO_TITLES} descriptions={SEO_DESC} image="/photos/prieure-cross.jpg" />
      {/* HERO */}
      <section className="relative" style={{ height: "48vh", minHeight: 340 }}>
        <img src="/photos/prieure-cross.jpg" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, rgba(20,16,12,0.55), rgba(20,16,12,0.35))" }} />
        <div className="container-prieure relative h-full flex flex-col justify-end pb-12">
          <div className="font-caps text-[0.7rem] mb-3" style={{ color: "var(--gold)", letterSpacing: "0.28em" }}>{tr.kicker}</div>
          <h1 className="font-display text-4xl sm:text-6xl text-white">{tr.title}</h1>
        </div>
      </section>

      {/* Intro — retirée le 2026-02-19 à la demande de l'admin */}

      {/* MAP + ADDRESS */}
      <section className="py-12" style={{ background: "var(--parchment)" }}>
        <div className="container-prieure grid grid-cols-1 md:grid-cols-12 gap-8 items-stretch">
          <aside className="md:col-span-4">
            <div className="p-7 h-full flex flex-col" style={{ background: "var(--ink)", color: "var(--parchment)", borderRadius: 2, boxShadow: "0 10px 30px rgba(20,16,12,0.25)" }}>
              <div className="font-caps text-[0.6rem] mb-4" style={{ color: "var(--gold)", letterSpacing: "0.24em" }}>◇ {tr.addressTitle} ◇</div>
              <div className="font-display text-2xl leading-tight mb-2">{tr.address}</div>
              <div className="font-caps text-[0.65rem] mb-6" style={{ color: "rgba(245,240,228,0.75)", letterSpacing: "0.18em" }}>{tr.gps}</div>
              <div className="flex flex-col gap-2 mt-auto">
                <a href={MAPS_LINK}  target="_blank" rel="noreferrer noopener" data-testid="access-google-link"
                   className="btn btn-primary text-center" style={{ fontSize: "0.7rem", padding: "0.7rem 0.9rem", letterSpacing: "0.08em" }}>{tr.openInMaps}</a>
                <a href={WAZE_LINK}  target="_blank" rel="noreferrer noopener" data-testid="access-waze-link"
                   className="btn btn-outline text-center" style={{ fontSize: "0.7rem", padding: "0.7rem 0.9rem", letterSpacing: "0.08em", color: "var(--parchment)", borderColor: "rgba(245,240,228,0.55)" }}>{tr.openInWaze}</a>
                <a href={APPLE_LINK} target="_blank" rel="noreferrer noopener" data-testid="access-apple-link"
                   className="btn btn-outline text-center" style={{ fontSize: "0.7rem", padding: "0.7rem 0.9rem", letterSpacing: "0.08em", color: "var(--parchment)", borderColor: "rgba(245,240,228,0.55)" }}>{tr.openInApple}</a>
              </div>
            </div>
          </aside>
          <div className="md:col-span-8" data-testid="access-map-wrap">
            <div className="w-full overflow-hidden" style={{ borderRadius: 2, border: "1px solid var(--border)", boxShadow: "0 10px 30px rgba(20,16,12,0.15)" }}>
              <iframe
                title="Google Maps — Prieuré saint Léonard"
                data-testid="access-google-map"
                src={MAPS_EMBED}
                width="100%"
                height="460"
                style={{ border: 0, display: "block" }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      </section>

      {/* TRANSPORTS */}
      <section className="py-20" style={{ background: "white" }}>
        <div className="container-prieure">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <Ornament className="mb-6" />
            <h2 className="font-display text-4xl sm:text-5xl">{tr.transportsTitle}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {transports.map(({ Icon: TI, title, body, testid }, i) => (
              <article key={i} data-testid={`access-transport-${testid}`} className="bg-white p-7 border" style={{ borderColor: "var(--border)" }}>
                <div className="mb-5" style={{ color: "var(--gold)" }}><TI /></div>
                <h3 className="font-display text-2xl mb-3">{title}</h3>
                <p className="font-body text-[0.95rem]" style={{ color: "var(--ink-2)" }}>{body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* SHUTTLE — special highlight section */}
      <section data-testid="access-shuttle-section" className="py-20" style={{ background: "var(--ink)", color: "var(--parchment)" }}>
        <div className="container-prieure grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-5">
            <div style={{ color: "var(--gold)" }}><I.shuttle /></div>
            <div className="font-caps text-[0.62rem] mt-4 inline-block px-3 py-1 border"
                 style={{ color: "var(--gold)", borderColor: "var(--gold)", letterSpacing: "0.24em" }}>
              {tr.shuttleBadge}
            </div>
            <h2 className="font-display text-3xl sm:text-4xl mt-5 leading-tight">{tr.shuttleTitle}</h2>
          </div>
          <div className="md:col-span-7 md:border-l md:pl-10" style={{ borderColor: "rgba(212,175,55,0.3)" }}>
            <p className="font-body text-lg mb-6" style={{ color: "rgba(245,240,228,0.9)" }}>{tr.shuttleBody}</p>
            <ul className="space-y-3">
              {tr.shuttleStops.map((s, i) => (
                <li key={i} className="flex items-start gap-3 font-body">
                  <span className="font-display" style={{ color: "var(--gold)", lineHeight: 1.4 }}>◇</span>
                  <span style={{ color: "rgba(245,240,228,0.95)" }}>{s}</span>
                </li>
              ))}
            </ul>
            <p className="font-display italic text-sm mt-6" style={{ color: "rgba(245,240,228,0.7)" }}>{tr.shuttleNote}</p>
          </div>
        </div>
      </section>

      {/* PARKING + ACCESSIBILITY */}
      <section className="py-20" style={{ background: "var(--parchment)" }}>
        <div className="container-prieure grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          <article className="bg-white p-8 border" style={{ borderColor: "var(--border)" }} data-testid="access-parking">
            <h3 className="font-display text-2xl mb-3">{tr.parkingTitle}</h3>
            <p className="font-body" style={{ color: "var(--ink-2)" }}>{tr.parkingBody}</p>
          </article>
          <article className="bg-white p-8 border" style={{ borderColor: "var(--border)" }} data-testid="access-accessibility">
            <h3 className="font-display text-2xl mb-3">{tr.accessibilityTitle}</h3>
            <p className="font-body" style={{ color: "var(--ink-2)" }}>{tr.accessibilityBody}</p>
          </article>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16" style={{ background: "white" }}>
        <div className="container-prieure max-w-2xl text-center">
          <h3 className="font-display text-2xl mb-5">{tr.questionsTitle}</h3>
          <a href="/contact" data-testid="access-contact-cta" className="btn btn-primary" style={{ fontSize: "0.75rem", padding: "0.8rem 1.4rem", letterSpacing: "0.18em" }}>
            {tr.questionsCta}
          </a>
        </div>
      </section>
    </div>
  );
}
